import { useCallback, useRef } from 'react';

import type { ValueOf } from 'type-fest';

import { useTracking } from '@change-corgi/core-react/tracking';

import type { NOTIFICATION_TYPES } from '../../constants';
import { useDismissedNotificationsStorage } from '../useDismissedNotificationsStorage';

import { useAutoTaggedPetition } from './useAutoTaggedPetition';
import { useDecisionMakerRecommendation } from './useDecisionMakerRecommendation';
import { useEmailDecisionMaker } from './useEmailDecisionMaker';
import { useFreePromotionFulfillment } from './useFreePromotionFulfillment';

type DashboardNotification = { type: ValueOf<typeof NOTIFICATION_TYPES> };
type UseNotificationsData = { notifications: DashboardNotification[] };
type UseNotificationsActions = {
	trackNotificationView: (notification: DashboardNotification['type']) => void;
	dismissNotification: (notification: DashboardNotification['type']) => void;
};
type UseNotificationsReturn = ModelHookResult<UseNotificationsData, UseNotificationsActions>;
export function useNotificationsCenter(): UseNotificationsReturn {
	const { isDismissed, dismissNotification: dismissNotificationInStorage } = useDismissedNotificationsStorage();

	const freePromotionFulfillmentNotification = useFreePromotionFulfillment();
	const decisionMakerRecommendationNotification = useDecisionMakerRecommendation();
	const autoTaggedPetitionNotification = useAutoTaggedPetition();
	const emailDecisionMakerNotification = useEmailDecisionMaker();

	const notifications = [
		freePromotionFulfillmentNotification,
		decisionMakerRecommendationNotification,
		autoTaggedPetitionNotification,
		emailDecisionMakerNotification,
	]
		.filter(Boolean)
		.filter((notification) => !isDismissed(notification.type));

	const track = useTracking();
	const trackedNotifications = useRef<Array<DashboardNotification['type']>>([]);
	const trackNotificationView = (notification: DashboardNotification['type']) => {
		if (trackedNotifications.current.includes(notification)) return;
		void track('petition_manage_notification_view', { notification });
		trackedNotifications.current.push(notification);
	};

	const dismissNotification = useCallback(
		(notification: DashboardNotification['type']) => {
			dismissNotificationInStorage(notification);
		},
		[dismissNotificationInStorage],
	);

	return {
		data: { notifications },
		actions: { trackNotificationView, dismissNotification },
	};
}
